import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ScannerProvider } from "@/contexts/ScannerContext";
import { BarcodeGlobalProvider } from "@/contexts/BarcodeGlobalContext";
import { GlobalBarcodeActionPanel } from "@/components/Barcode/GlobalBarcodeActionPanel";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UpdateDialog } from "@/components/UpdateDialog";
import { LiveUpdateBanner } from "@/components/LiveUpdateBanner";
import { NotificationProvider } from "@/components/Notifications";
import { DataChangeListener } from "@/components/DataChangeListener";
import { AIFloatingChat } from "@/components/AI";
import Index from "./pages/Index";
import CustomizableDashboard from "./pages/CustomizableDashboard";
import Analytics from "./pages/Analytics";
import NFCCaptures from "./pages/NFCCaptures";
import NFCTagsAdmin from "./pages/NFCTagsAdmin";
import WorkOrders from "./pages/WorkOrders";
import Tasks from "./pages/Tasks";
import QualityControl from "./pages/QualityControl";
import OperatorView from "./pages/OperatorView";
import OperatorAssignment from "./pages/OperatorAssignment";
import Integrations from "./pages/Integrations";
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import RegisterUser from "./pages/RegisterUser";
import QRTraceability from "./pages/QRTraceability";
import Molds from "./pages/Molds";
import ProductionRecipes from "./pages/ProductionRecipes";
import Customers from "./pages/Customers";
import CustomerDetails from "./pages/CustomerDetails";
import News from "./pages/News";
import Machines from "./pages/Machines";
import Albums from "./pages/Albums";
import Links from "./pages/Links";
import NotFound from "./pages/NotFound";
import InstallApp from "./pages/InstallApp";
import Landing from "./pages/Landing";
import WorkTypes from "./pages/WorkTypes";
import Personal from "./pages/Personal";
import Tools from "./pages/Tools";
import Projects from "./pages/Projects";
import Budgets from "./pages/Budgets";
import PurchaseOrders from "./pages/PurchaseOrders";
import Products from "./pages/Products";
import DesignProjects from "./pages/DesignProjects";
import Masters from "./pages/Masters";
import MachiningTools from "./pages/MachiningTools";
import CutMaterial from "./pages/CutMaterial";
import WorkshopCutting from "./pages/WorkshopCutting";
import WorkshopShelves from "./pages/WorkshopShelves";
import InspectorStation from "./pages/InspectorStation";
import CuttingStation from "./pages/CuttingStation";
import AssemblyStation from "./pages/AssemblyStation";
import PaintingStation from "./pages/PaintingStation";
import PaintBooths from "./pages/PaintBooths";
import Materials from "./pages/Materials";
import EventsPage from "./pages/Events";
import ManufacturingProcesses from "./pages/ManufacturingProcesses";
import Traceability from "./pages/Traceability";
import Reports from "./pages/Reports";
import TraceabilityHistory from "./pages/TraceabilityHistory";
import SupportTickets from "./pages/SupportTickets";
import MyProfile from "./pages/MyProfile";
import UserManagementAdvanced from "./pages/UserManagementAdvanced";
import PlotterCuts from "./pages/PlotterCuts";
import FreezerControl from "./pages/FreezerControl";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import Quotations from "./pages/Quotations";
import Labels from "./pages/Labels";
import LegalDocuments from "./pages/LegalDocuments";
import ShiftManagement from "./pages/ShiftManagement";
import Fichaje from "./pages/Fichaje";
import FichajeAdmin from "./pages/FichajeAdmin";
import GlobalSearch from "./pages/GlobalSearch";
import Plybooks from "./pages/Plybooks";
import ProductionSchedule from "./pages/ProductionSchedule";
import ProductionPlanner from "./pages/ProductionPlanner";
import GanttPlanner from "./pages/GanttPlanner";
import ProductionKanban from "./pages/ProductionKanban";
import OperatorsManagement from "./pages/OperatorsManagement";
import OperatorWorkView from "./pages/OperatorWorkView";
import AutoclaveCycles from "./pages/AutoclaveCycles";
import CuringRecipes from "./pages/CuringRecipes";
import ProductionFlowSimulation from "./pages/ProductionFlowSimulation";
import Payroll from "./pages/Payroll";
import PayrollAdmin from "./pages/PayrollAdmin";
import RPL from "./pages/RPL";
import RPLIncidents from "./pages/RPLIncidents";
import RPLTraining from "./pages/RPLTraining";
import ComplianceDashboard from "./pages/ComplianceDashboard";
import RequirementsManagement from "./pages/RequirementsManagement";
import WorkPool from "./pages/WorkPool";
import BillingDashboard from "./pages/BillingDashboard";
import BillingCustomers from "./pages/BillingCustomers";
import Invoices from "./pages/Invoices";
import InvoiceEditor from "./pages/InvoiceEditor";
import InvoiceDetail from "./pages/InvoiceDetail";
import BillingSettings from "./pages/BillingSettings";
import DeliveryNotes from "./pages/DeliveryNotes";
import BillingArticles from "./pages/BillingArticles";
import Suppliers from "./pages/Suppliers";
import SupplierDetail from "./pages/SupplierDetail";
import SupplierOrders from "./pages/SupplierOrders";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import Expenses from "./pages/Expenses";
import JournalEntries from "./pages/JournalEntries";

import GeneralLedger from "./pages/GeneralLedger";
import TrialBalance from "./pages/TrialBalance";
import TraceabilityView from "./pages/TraceabilityView";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: 30000,  // 30 seconds instead of 5
      staleTime: 10000,        // Data fresh for 10 seconds
      gcTime: 300000,          // Cache for 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 2,                // Retry failed requests twice
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
            <ScannerProvider>
            <BarcodeGlobalProvider>
            <NotificationProvider>
              <DataChangeListener />
              <GlobalBarcodeActionPanel />
              <Routes>
              <Route path="/landing" element={<Landing />} />
              <Route path="/t/:code" element={<TraceabilityView />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/install" element={<InstallApp />} />
              <Route path="/" element={<ProtectedRoute><CustomizableDashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Index /></ProtectedRoute>} />
              <Route path="/operator" element={<ProtectedRoute><OperatorView /></ProtectedRoute>} />
              <Route path="/operator-assignment" element={<ProtectedRoute><OperatorAssignment /></ProtectedRoute>} />
              <Route path="/work-orders" element={<ProtectedRoute requiredModule="work-orders"><WorkOrders /></ProtectedRoute>} />
              <Route path="/work-pool" element={<ProtectedRoute><WorkPool /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute requiredModule="tasks"><Tasks /></ProtectedRoute>} />
              <Route path="/quality-control" element={<ProtectedRoute requiredModule="quality-control"><QualityControl /></ProtectedRoute>} />
              <Route path="/nfc-captures" element={<ProtectedRoute><NFCCaptures /></ProtectedRoute>} />
              <Route path="/nfc-tags-admin" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><NFCTagsAdmin /></ProtectedRoute>} />
              <Route path="/qr-traceability" element={<ProtectedRoute requiredModule="traceability"><QRTraceability /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute allowedRoles={['admin']}><Integrations /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><Settings /></ProtectedRoute>} />
              <Route path="/register-user" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><RegisterUser /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><UserManagementAdvanced /></ProtectedRoute>} />
              <Route path="/my-profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
              <Route path="/molds" element={<ProtectedRoute><Molds /></ProtectedRoute>} />
              <Route path="/recipes" element={<ProtectedRoute><ProductionRecipes /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
              <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetails /></ProtectedRoute>} />
              <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
              <Route path="/work-types" element={<ProtectedRoute><WorkTypes /></ProtectedRoute>} />
              <Route path="/machines" element={<ProtectedRoute><Machines /></ProtectedRoute>} />
              <Route path="/albums" element={<ProtectedRoute><Albums /></ProtectedRoute>} />
              <Route path="/links" element={<ProtectedRoute><Links /></ProtectedRoute>} />
              <Route path="/personal" element={<ProtectedRoute><Personal /></ProtectedRoute>} />
              <Route path="/shifts" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><ShiftManagement /></ProtectedRoute>} />
              <Route path="/fichaje" element={<ProtectedRoute><Fichaje /></ProtectedRoute>} />
              <Route path="/fichaje/admin" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><FichajeAdmin /></ProtectedRoute>} />
              <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
              <Route path="/budgets" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><Budgets /></ProtectedRoute>} />
              <Route path="/purchase-orders" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><PurchaseOrders /></ProtectedRoute>} />
              <Route path="/products" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><Products /></ProtectedRoute>} />
              <Route path="/design" element={<ProtectedRoute><DesignProjects /></ProtectedRoute>} />
              <Route path="/masters" element={<ProtectedRoute><Masters /></ProtectedRoute>} />
              <Route path="/machining-tools" element={<ProtectedRoute><MachiningTools /></ProtectedRoute>} />
              <Route path="/materials" element={<ProtectedRoute><Materials /></ProtectedRoute>} />
              <Route path="/material-inventory" element={<ProtectedRoute><Navigate to="/materials" replace /></ProtectedRoute>} />
              <Route path="/cut-material" element={<ProtectedRoute><CutMaterial /></ProtectedRoute>} />
              <Route path="/workshop-cutting" element={<ProtectedRoute><WorkshopCutting /></ProtectedRoute>} />
              <Route path="/workshop-shelves" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><WorkshopShelves /></ProtectedRoute>} />
              <Route path="/inspector-station" element={<ProtectedRoute allowedRoles={["admin", "supervisor", "operator"]}><InspectorStation /></ProtectedRoute>} />
              <Route path="/cutting-station" element={<ProtectedRoute allowedRoles={["admin", "supervisor", "operator"]}><CuttingStation /></ProtectedRoute>} />
              <Route path="/assembly-station" element={<ProtectedRoute allowedRoles={["admin", "supervisor", "operator"]}><AssemblyStation /></ProtectedRoute>} />
              <Route path="/painting-station" element={<ProtectedRoute allowedRoles={["admin", "supervisor", "operator"]}><PaintingStation /></ProtectedRoute>} />
              <Route path="/paint-booths" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><PaintBooths /></ProtectedRoute>} />
              <Route path="/plotter-cuts" element={<ProtectedRoute><PlotterCuts /></ProtectedRoute>} />
              <Route path="/freezer-control" element={<ProtectedRoute><FreezerControl /></ProtectedRoute>} />
              <Route path="/raw-materials" element={<ProtectedRoute><Navigate to="/materials" replace /></ProtectedRoute>} />
              <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
              <Route path="/processes" element={<ProtectedRoute requiredModule="processes"><ManufacturingProcesses /></ProtectedRoute>} />
              <Route path="/traceability" element={<ProtectedRoute requiredModule="traceability"><Traceability /></ProtectedRoute>} />
              <Route path="/executive" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><ExecutiveDashboard /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/traceability-history" element={<ProtectedRoute><TraceabilityHistory /></ProtectedRoute>} />
              <Route path="/support-tickets" element={<ProtectedRoute><SupportTickets /></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><GlobalSearch /></ProtectedRoute>} />
              <Route path="/quotations" element={<ProtectedRoute requiredModule="quotations"><Quotations /></ProtectedRoute>} />
              <Route path="/labels" element={<ProtectedRoute><Labels /></ProtectedRoute>} />
              <Route path="/plybooks" element={<ProtectedRoute><Plybooks /></ProtectedRoute>} />
              <Route path="/schedule" element={<ProtectedRoute><ProductionSchedule /></ProtectedRoute>} />
              <Route path="/production-planner" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><ProductionPlanner /></ProtectedRoute>} />
              <Route path="/gantt-planner" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><GanttPlanner /></ProtectedRoute>} />
              <Route path="/production-kanban" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><ProductionKanban /></ProtectedRoute>} />
              <Route path="/operators-management" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><OperatorsManagement /></ProtectedRoute>} />
              <Route path="/my-work" element={<ProtectedRoute><OperatorWorkView /></ProtectedRoute>} />
              <Route path="/flow-simulation" element={<ProtectedRoute><ProductionFlowSimulation /></ProtectedRoute>} />
              <Route path="/autoclave-cycles" element={<ProtectedRoute><AutoclaveCycles /></ProtectedRoute>} />
              <Route path="/curing-recipes" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><CuringRecipes /></ProtectedRoute>} />
              <Route path="/legal-documents" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><LegalDocuments /></ProtectedRoute>} />
              <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
              <Route path="/payroll-admin" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><PayrollAdmin /></ProtectedRoute>} />
              <Route path="/rpl" element={<ProtectedRoute requiredModule="rpl"><RPL /></ProtectedRoute>} />
              <Route path="/rpl/incidents" element={<ProtectedRoute requiredModule="rpl"><RPLIncidents /></ProtectedRoute>} />
              <Route path="/rpl/training" element={<ProtectedRoute requiredModule="rpl"><RPLTraining /></ProtectedRoute>} />
              <Route path="/compliance" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><ComplianceDashboard /></ProtectedRoute>} />
              <Route path="/requirements" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><RequirementsManagement /></ProtectedRoute>} />
              {/* Billing / Facturación */}
              <Route path="/billing" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><BillingDashboard /></ProtectedRoute>} />
              <Route path="/billing/customers" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><BillingCustomers /></ProtectedRoute>} />
              <Route path="/billing/invoices" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><Invoices /></ProtectedRoute>} />
              <Route path="/billing/invoices/new" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><InvoiceEditor /></ProtectedRoute>} />
              <Route path="/billing/invoices/:id" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><InvoiceDetail /></ProtectedRoute>} />
              <Route path="/billing/invoices/:id/edit" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><InvoiceEditor /></ProtectedRoute>} />
              <Route path="/billing/settings" element={<ProtectedRoute allowedRoles={['admin']}><BillingSettings /></ProtectedRoute>} />
              <Route path="/billing/delivery-notes" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><DeliveryNotes /></ProtectedRoute>} />
              <Route path="/billing/articles" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><BillingArticles /></ProtectedRoute>} />
              <Route path="/billing/supplier-orders" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><SupplierOrders /></ProtectedRoute>} />
              <Route path="/billing/suppliers" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><Suppliers /></ProtectedRoute>} />
              <Route path="/billing/suppliers/:id" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><SupplierDetail /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><Expenses /></ProtectedRoute>} />
              <Route path="/accounting/chart" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><ChartOfAccounts /></ProtectedRoute>} />
              <Route path="/accounting/journal" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><JournalEntries /></ProtectedRoute>} />
              <Route path="/accounting/ledger" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><GeneralLedger /></ProtectedRoute>} />
              <Route path="/accounting/trial-balance" element={<ProtectedRoute allowedRoles={["admin", "supervisor"]}><TrialBalance /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
              </Routes>
            <AIFloatingChat />
            </NotificationProvider>
            </BarcodeGlobalProvider>
            </ScannerProvider>
            </AuthProvider>
          </BrowserRouter>
          <OfflineIndicator />
          <LiveUpdateBanner />
          <UpdateDialog />
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
